import { graphQlClient } from "lib/graphql-client.js";
import Layout from "../../components/Layout";
import dynamic from "next/dynamic";

import { site } from "../../config";

import MainSlider from "components/Slider/BlazeSlider/BlazeSlider";
import { generateBlazeSlides } from "utils/sliders";

const FeaturedBlog = dynamic(
  () => import("../../components/BlogPage/FeaturedBlog")
);
import {
  blogPageQuery,
  storyCategoriesQuery,
} from "lib/graphql/article-queries";
import { fetchLatestStories, processStoryData } from "lib/api/strapi-stories";

import Tabs from "components/ui/Tabs/Tabs";
import Tab from "components/ui/Tabs/Tab";

import CardRow from "components/card/CardRow";

import { fetchLayoutData, fetchLayoutDataServer } from "lib/api/strapi-layout";

import styles from "./StoryFront.module.scss";
import StoryLinkCard from "@/components/card/StoryLinkCard";

const BlogMain = ({ blogs, layoutData }) => {
  const SEO = {
    canonicalURL: site + "/stories",
    ...blogs?.blogMain?.search_engine_optimization,
  };

  const featuredBlogImageSmall =
    blogs?.blogMain?.featured_blog?.HeroSlider?.[0]?.sliderImageLarge?.formats
      ?.large?.url ??
    blogs?.blogMain?.featured_blog?.HeroSlider?.[0]?.sliderImageSmall?.url;

  const featuredBlogImageLarge =
    blogs?.blogMain?.featured_blog?.HeroSlider?.[0]?.sliderImageLarge?.formats
      ?.large?.url ??
    blogs?.blogMain?.featured_blog?.HeroSlider?.[0]?.sliderImageLarge?.url;

  function generateStoryTabs() {
    const storiesByCategory = blogs?.storyCategories.map((category) => {
      const stories = blogs?.articles.filter((article) => {
        const categories = article.story_category.map(
          (storyCategory) => storyCategory.category_name
        );
        return categories.includes(category.category_name);
      });

      return { category, stories };
    });

    const storyTabs = storiesByCategory
      .filter((storiesObject) => storiesObject.stories.length > 0)
      .map((storiesObject, index) => {
        const linkCards = storiesObject.stories.map((story, index) => (
          <StoryLinkCard story={story} key={`${story.id}=${index}`} />
        ));

        const tabCards = (
          <CardRow
            key={`${storiesObject.category.category_name}-cardrow`}
            cards={linkCards}
            sectionTitle="Latest Stories"
            // sectionSubTitle="Get inspired, get involved."
            sectionSlug="/stories"
            seeAll={false}
            sideScrollButtons={false}
            scrollIndicator={false}
          ></CardRow>
        );

        const tab = (
          <Tab
            title={
              storiesObject.category.category_name[0].toUpperCase() +
              storiesObject.category.category_name.slice(
                1,
                storiesObject.category.category_name.length
              )
            }
            key={`category-${index}`}
          >
            <div className={`${styles.tabCardsContainer}`}>{tabCards}</div>
          </Tab>
        );
        return tab;
      });

    return storyTabs;
  }

  const storyTabs = generateStoryTabs();

  const allCategoriesContent = blogs?.articles.map((story, index) => {
    return <StoryLinkCard story={story} key={`${story.id}-all-${index}`} />;
  });

  const allCategoriesTab = (
    <div className={`${styles.tabCardsContainer}`}>
      <CardRow
        cards={allCategoriesContent}
        sectionTitle="Latest Stories"
        headingType="h1"
        // sectionSubTitle="Get inspired, get involved."
        sectionSlug="/stories"
        seeAll={false}
        cardsPerRow={3}
        sideScrollButtons={false}
        scrollIndicator={false}
      ></CardRow>
    </div>
  );

  const slides = generateBlazeSlides({
    slides: blogs?.blogMain?.blogs_for_slider,
    type: "blog",
    ctaButton: true,
  });

  return (
    <Layout seo={SEO} layoutData={layoutData}>
      <div className={`hero-container ${styles.heroContainer}`}>
        <MainSlider sideNav={true}>{slides}</MainSlider>
      </div>

      <main className={`variable-container ${styles.mainContainer}`}>
        <Tabs tabButtonStyle="mini">
          <Tab title="All">{allCategoriesTab}</Tab>
          {storyTabs}
        </Tabs>

        <FeaturedBlog
          title={blogs?.blogMain?.featured_blog?.title}
          description={blogs?.blogMain?.featured_blog?.description}
          imageLarge={featuredBlogImageLarge}
          imageSmall={featuredBlogImageSmall}
          slug={blogs?.blogMain?.featured_blog?.slug}
          date={blogs?.blogMain?.featured_blog?.createdAt}
          author={blogs?.blogMain?.featured_blog?.author?.authorName}
        />
      </main>
    </Layout>
  );
};

export default BlogMain;

export async function getStaticProps(content) {
  const client = graphQlClient();

  const blogMainData = await client.query({
    query: blogPageQuery,
  });

  const blogMain = {
    ...blogMainData?.data?.blogMain?.data?.attributes,
    blogs_for_slider:
      blogMainData?.data?.blogMain?.data?.attributes?.blogs_for_slider?.data?.map(
        (story) => {
          return processStoryData(story, { type: "full" });
        }
      ),
    featured_blog: processStoryData(
      blogMainData?.data?.blogMain?.data?.attributes?.featured_blog?.data,
      { type: "full" }
    ),
  };

  const articles = await fetchLatestStories({ start: 0, limit: 100 });

  const storyCategoriesData = await client.query({
    query: storyCategoriesQuery,
  });
  const storyCategories = storyCategoriesData?.data?.storyCategories?.data.map(
    (category) => {
      return {
        ...category?.attributes,
        id: category?.id,
      };
    }
  );

  const blogData = {
    articles,
    blogMain,
    storyCategories,
  };

  // const layoutData = await fetchLayoutData();
  const layoutData = await fetchLayoutDataServer();

  return {
    props: {
      blogs: blogData,
      layoutData,
    },
  };
}
